import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTextArea, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import ModalSide from "src/components/Modal/styles";
import Select from "src/components/Select";
import { useAuth } from "src/context/Auth/context";
import formatDate from "src/utils/formatDate";
import Title from "src/components/Title";
import Pagination from "src/components/Pagination";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { IComponentSuggestionsModals, IRefSuggestionsModals, ISuggestions } from "./interface";
import addAndChangeSchema, { addCommentSchema } from "./schemas";
import { badgeSuggestions, tagsOfSuggestions } from ".";

const Modals: React.ForwardRefRenderFunction<IRefSuggestionsModals, IComponentSuggestionsModals> = ({
	reload,
	CustomServicesApi,
	optionsMarkers
}, ref) => {
	const { translate, currentLanguage } = useTranslation();
	const {businessGroup, user} = useAuth();
	const {addToast} = RCCHookToast();
	const [selectedSuggestion, setSelectedSuggestion] = useState<ISuggestions>({} as ISuggestions);
	const [comments, setComments] = useState<any[]>([]);
	
	const paginationRef = useRef<IForwardPagination>(null);

	// Modal Add/Change
	const modalAdd = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitAdd, 
		register : registerAdd, 
		reset : resetAdd, 
		setError : setErrorAdd, 
		formState: { errors : errorsAdd }, 
		clearErrors: clearErrorsAdd,
		control: controlAdd 
	} = useForm();

	const submitAdd = async (data: any) => {
		try{
			clearErrorsAdd();
			modalAdd.current?.setLoadingButton();
			await addAndChangeSchema(translate).validate(data, {
				abortEarly: false
			});

			let bookmarks = "";

			if(data.markers && data.markers !== null && data.markers.length > 0)
				bookmarks = data.markers.map((mark: any) => mark.value).join(",");

			const formData = new FormData();
			formData.append("sistema", "14");
			formData.append("empresa", businessGroup.efa001Id.toString());
			formData.append("usuario", user.efa005Id.toString());
			formData.append("titulo", data.title);
			formData.append("descricao", data.description);
			formData.append("marcadores", bookmarks);

			await CustomServicesApi.postRequest({
				method: "Sugestoes/InserirSugestao",
				contentType: "multipart/form-data",
				body: formData
			});
			
			addToast({
				color: "green",
				description: translate("Sugestão adicionada com sucesso"),
				title: translate("Sucesso")
			});

			RCCServicesApi.postRequest({
				method: "Suggestions/NotifySuggestion",
				body: {
					title: data.title,
					description: data.description
				}
			});

			modalAdd.current?.resetLoadingButton();
			modalAdd.current?.closeModal();
			reload();

		}catch(err){

			modalAdd.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAdd))
				return;

			standardErrorMessages(err, translate("Erro ao adicionar sugestão"), addToast);
		}
	};
	const actionButtonAdd = () => {
		resetAdd(); 
		modalAdd.current?.openModal();
	};

	const modalView = useRef<RCCRefModalProps>(null);
	const loadComments = async (id: number) => {
		const response = await CustomServicesApi.getRequest({
			method: "Sugestoes/ListarComentarios",
			urlParameters:{
				sugestaoId: id,
				p: 1,
				q: 1000
			}
		});

		setComments(response.data.list);
		paginationRef.current?.reload();
	};
	const actionButtonView = async (suggestion: ISuggestions) => {
		try{
			setSelectedSuggestion(suggestion);
			modalView.current?.openModal();
			
			await loadComments(suggestion.Sga001Id);

		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar sugestão"), addToast);
		}
	};
	const colComments = (row: any) => (
		<RCCGrid container item direction="column">
			<span>{row.Sga002Descricao}</span>
			<span>{translate("Postada em")} {formatDate(new Date(Number(row.Sga002Data.replace(/\D/g,""))), currentLanguage, true)}</span>
		</RCCGrid>
	);
	const evaluateSuggestion = async (isLike?: boolean) => {
		try{
			const formData = new FormData();
			formData.append("sistema", "14");
			formData.append("empresa", businessGroup.efa001Id.toString());
			formData.append("usuario", user.efa005Id.toString());
			formData.append("sugestaoId", selectedSuggestion.Sga001Id.toString());

			if(isLike)
				formData.append("avaliacao", selectedSuggestion.AvaliacaoUsuario !== 1 ? "1" : "0");
			else
				formData.append("avaliacao", selectedSuggestion.AvaliacaoUsuario !== -1 ? "-1" : "0");

			const {data} = await CustomServicesApi.postRequest({
				method: "Sugestoes/AvaliarSugestao",
				contentType: "multipart/form-data",
				body: formData
			});
			
			addToast({
				color: "green",
				description: translate("Sugestão avaliada com sucesso"),
				title: translate("Sucesso")
			});
			
			setSelectedSuggestion((prevState) => ({
				...prevState,
				AvaliacaoUsuario: data.ResponseObject.AvaliacaoUsuario,
				QtdeAvaliacoesNegativas: data.ResponseObject.QtdeAvaliacoesNegativas,
				QtdeAvaliacoesPositivas: data.ResponseObject.QtdeAvaliacoesPositivas
			}));

		}catch(err){
			standardErrorMessages(err, translate("Erro ao avaliar sugestão"), addToast);
		}
	};

	const modalComment = useRef<RCCRefModalProps>(null);
	const { 
		handleSubmit : handleSubmitComment, 
		register : registerComment, 
		reset : resetComment, 
		setError : setErrorComment, 
		formState: { errors : errorsComment }, 
		clearErrors: clearErrorsComment
	} = useForm();
	const submitComment = async (data: any) => {
		try{
			clearErrorsComment();
			modalComment.current?.setLoadingButton();
			await addCommentSchema(translate).validate(data, {
				abortEarly: false
			});

			const formData = new FormData();
			formData.append("sistema", "14");
			formData.append("empresa", businessGroup.efa001Id.toString());
			formData.append("usuario", user.efa005Id.toString());
			formData.append("sugestaoId", selectedSuggestion.Sga001Id.toString());
			formData.append("descricao", data.description);

			await CustomServicesApi.postRequest({
				method: "Sugestoes/InserirComentario",
				contentType: "multipart/form-data",
				body: formData
			});
			
			addToast({
				color: "green",
				description: translate("Comentário adicionado com sucesso"),
				title: translate("Sucesso")
			});

			modalComment.current?.resetLoadingButton();
			modalComment.current?.closeModal();

			await loadComments(selectedSuggestion.Sga001Id);
		}catch(err){

			modalComment.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorComment))
				return;

			standardErrorMessages(err, translate("Erro ao adicionar comentário"), addToast);
		}
	};
	const openModalComment = () => {
		resetComment();
		modalComment.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonView
	}));
    
	return (
		<>	
			{/* Modal ADD */}
			<Modal
				ref={modalAdd}
				header={{
					title: translate("Nova sugestão"),
					icon: {
						name: "thumbs"
					}
				}}
				onSubmit={handleSubmitAdd(submitAdd)}
				styleInline={ModalSide}
			>
				<RCCGrid
					container
					item
					direction="column"
					alignItems="center"
				>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCInput 
							register={registerAdd}
							name="title"
							label={translate("Título")}
							maxLength={60}
							error={errorsAdd.title}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<RCCTextArea 
							register={registerAdd}
							name="description"
							label={translate("Descrição")}
							maxLength={2000}
							error={errorsAdd.title}
							required
						/>
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
					>
						<Select 
							name="markers"						
							control={controlAdd}
							label={translate("Marcadores")}
							isMulti
							options={optionsMarkers} 
						/>
					</RCCGrid>
				</RCCGrid>
			</Modal>

			<Modal
				ref={modalView}
				header={{
					title: translate("Detalhes da sugestão"),
					icon: {
						name: "thumbs"
					}
				}}
				footer={{
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Sim"),
					cancelButtonText: translate("Fechar"),
					showSaveButton: false
				}}
				onClose={reload}
				styleInline={`${ModalSide} form{
					>div{
						overflow-x: hidden;
					}
				}`}
			>
				<RCCGrid container item justifyContent="flex-end">
					{badgeSuggestions(selectedSuggestion.Sga001Situacao, translate)}
				</RCCGrid>
				<Title color="purple" size="lg">
					{selectedSuggestion.Sga001Titulo}
				</Title>
				<Title color="defaultColor" marginTop={8} marginBottom={8}>
					{selectedSuggestion.Sga001Descricao}
				</Title>
				<RCCGrid container item alignItems="center">
					{tagsOfSuggestions(selectedSuggestion.Sgtba004, translate)}
				</RCCGrid>
				<RCCGrid container item alignItems="baseline" justifyContent="space-between">
					<small style={{ fontSize: "0.7rem" }}>
						{selectedSuggestion.Sga001Data && selectedSuggestion.Sga001Data !== null && <>
							{translate("Postada em")} {formatDate(new Date(Number(selectedSuggestion.Sga001Data.replace(/\D/g,""))), currentLanguage, true)}
						</>}
					</small>
					<div style={{ display: "flex"}}>
						<RCCButton 
							color="green"
							outlined={selectedSuggestion.AvaliacaoUsuario !== 1}
							icon={{
								name: "thumbs"
							}}
							onClick={() => evaluateSuggestion(true)}
							style={{minWidth: "52px", justifyContent: "space-between"}}
						>
							{selectedSuggestion.QtdeAvaliacoesPositivas}
						</RCCButton>
						<RCCButton 
							color="red"
							outlined={selectedSuggestion.AvaliacaoUsuario !== -1}
							icon={{
								name: "thumbsDown"
							}}
							onClick={() => evaluateSuggestion(false)}
							style={{minWidth: "52px", justifyContent: "space-between"}}
						>
							{selectedSuggestion.QtdeAvaliacoesNegativas * -1}
						</RCCButton>
						<RCCTooltip
							color="purple"
							text={translate("Adicionar comentário")}
						>
							<RCCButton 
								color="purple"
								icon={{
									name: "commentFilled"
								}}
								onClick={openModalComment}
								style={{ height: "27.5px" }}
							/>
						</RCCTooltip>
					</div>
				</RCCGrid>

				<Pagination 
					ref={paginationRef}
					dataOrRoute={comments}    
					columns={[
	  					{
							idColumn: "title",
							nameColumn: translate("Comentários"),
							xs: 12,
							cellRenderer: colComments
	  					}
					]}
				/>
			</Modal>

			<Modal
				ref={modalComment}
				header={{
					title: translate("Adicionar comentário"),
					icon: {
						name: "commentFilled"
					}
				}}
				onSubmit={handleSubmitComment(submitComment)}
				styleInline="max-height: 240px; position: absolute; top: 60px;"
			>
				<RCCTextArea 
					register={registerComment}
					label={translate("Descrição")}
					name="description"
					maxLength={4000}
					required
					error={errorsComment.description}
				/>
			</Modal>
		</>
	);
};

export default forwardRef(Modals);
